import { cn } from '@/lib/utils';
import {
  extractTemplateVariables,
  renderTemplateWithVariables,
} from '@/lib/whatsapp-templates/preview-body';
import type {
  WhatsAppTemplateButton,
  WhatsAppTemplateHeader,
} from '@/types/whatsapp-template';
import { FileText, Image, Video } from 'lucide-react';

interface TemplatePreviewProps {
  header?: WhatsAppTemplateHeader | null;
  body: string;
  footer?: string | null;
  buttons?: WhatsAppTemplateButton[];
  variables?: string[];
  variableValues?: Record<string, string>;
  className?: string;
}

function HeaderPreview({ header }: { header?: WhatsAppTemplateHeader | null }) {
  if (!header) return null;

  const type = String(header.type ?? 'text').toLowerCase();
  const text = typeof header.text === 'string' ? header.text : null;

  if (type === 'text' && text) {
    return <p className="font-semibold text-sm text-gray-900 mb-2">{text}</p>;
  }

  const iconClass = 'size-8 text-gray-400';
  const label =
    type === 'image'
      ? 'Image header'
      : type === 'video'
        ? 'Video header'
        : type === 'document'
          ? 'Document header'
          : text || 'Header';

  if (type === 'text') return null;

  return (
    <div className="mb-3 rounded-lg bg-gray-100 flex flex-col items-center justify-center gap-1 py-6">
      {type === 'image' && <Image className={iconClass} />}
      {type === 'video' && <Video className={iconClass} />}
      {type === 'document' && <FileText className={iconClass} />}
      <span className="text-xs text-gray-500">{label}</span>
    </div>
  );
}

export const TemplatePreview = ({
  header,
  body,
  footer,
  buttons = [],
  variables,
  variableValues,
  className,
}: TemplatePreviewProps) => {
  const previewBody = renderTemplateWithVariables(body, variableValues ?? {});
  const detectedVariables =
    variables && variables.length > 0
      ? variables
      : extractTemplateVariables(body, footer, header?.text as string);

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      <div className="rounded-xl border border-accent bg-[#e5ddd5] p-4 min-h-[280px]">
        <div className="max-w-[280px] ml-auto">
          <div className="rounded-lg rounded-tr-none bg-[#dcf8c6] shadow-sm overflow-hidden">
            <div className="p-3">
              <HeaderPreview header={header} />
              <p className="text-sm text-gray-900 whitespace-pre-wrap break-words">
                {previewBody || 'Your message will appear here…'}
              </p>
              {footer ? (
                <p className="text-xs text-gray-500 mt-2">{footer}</p>
              ) : null}
              <p className="text-[10px] text-gray-500 text-right mt-1">12:30</p>
            </div>
            {buttons.length > 0 ? (
              <div className="border-t border-[#c8e6b4] divide-y divide-[#c8e6b4]">
                {buttons.map((button, index) => (
                  <div
                    key={`${button.text}-${index}`}
                    className="py-2.5 text-center text-sm font-medium text-[#00a5f4]"
                  >
                    {button.type === 'url' && '🔗 '}
                    {button.type === 'phone' && '📞 '}
                    {button.text || `Button ${index + 1}`}
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {detectedVariables.length > 0 ? (
        <div className="rounded-lg border border-accent bg-muted/20 p-3">
          <p className="text-xs font-semibold text-muted-foreground mb-2">
            Variables
          </p>
          <div className="flex flex-wrap gap-2">
            {detectedVariables.map((variable) => (
              <span
                key={variable}
                className="rounded-md bg-primary/10 text-primary px-2 py-1 text-xs font-mono"
              >
                {variable.startsWith('{{') ? variable : `{{${variable}}}`}
              </span>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};
